import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronLeft, 
  ChevronRight,
  Anchor,
  Waves,
  Users,
  Home,
  Compass,
  Ship,
  ExternalLink,
  Bed,
  Maximize,
  Eye,
  AlignCenter
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";

// Reservation link
const RESERVATION_LINK = "https://be.heytravel.net/98ad1190-eac6-4d76-8a60-5b070bf8fa02?";

// Bungalow images from design guidelines
const bungalowImages = [
  "https://818.pt/media/quarto1.jpeg",
  "https://818.pt/media/quarto2.jpeg",
  "https://818.pt/media/quarto3.jpeg",
];

// Gallery images
const galleryImages = [
  "https://818.pt/media/foto1.jpeg",
  "https://818.pt/media/foto2.jpeg",
  "https://818.pt/media/foto3.jpeg",
  "https://818.pt/media/foto4.jpeg",
  "https://818.pt/media/foto5.jpeg",
  "https://818.pt/media/foto6.jpeg",
];

// 3 types of bungalows
const bungalowTypes = [
  {
    id: 1,
    name: "Bungalow - Um Quarto",
    type: "T1",
    maxGuests: 2,
    area: "30",
    category: "Villa",
    description: "Os apartamentos T1 são ideais para casais ou para pessoas solteiras que procuram a privacidade e a conveniência de um Bungalow de luxo, contando com o apoio das facilidades de um aldeamento com qualidade.",
    image: bungalowImages[0],
    photos: [
      bungalowImages[0],
      "https://818.pt/media/quarto1_1.jpeg",
      "https://818.pt/media/quarto1_2.jpeg",
      "https://818.pt/media/quarto1_3.jpeg",
    ],
  },
  {
    id: 2,
    name: "Bungalow - Dois Quartos",
    type: "T2",
    maxGuests: 4,
    area: "60",
    category: "Villa",
    description: "Os apartamentos T2 são ideais para famílias que procuram a privacidade e a conveniência de um Bungalow de luxo, contando com o apoio das facilidades de um aldeamento com qualidade.",
    image: bungalowImages[1],
    photos: [
      bungalowImages[1],
      "https://818.pt/media/quarto2_1.jpeg",
      "https://818.pt/media/quarto2_2.jpeg",
      "https://818.pt/media/quarto2_3.jpeg",
      bungalowImages[0],
    ],
  },
  {
    id: 3,
    name: "Bungalow - Dois Quartos - Vista Mar",
    type: "T2",
    maxGuests: 4,
    area: "60",
    category: "Villa",
    hasSeaView: true,
    description: "Os apartamentos T2 são ideais para famílias que procuram a privacidade e a conveniência de um Bungalow de luxo, contando com o apoio das facilidades de um aldeamento com qualidade.",
    image: bungalowImages[2],
    photos: [
      bungalowImages[2],
      "https://818.pt/media/quarto3_1.jpeg",
      "https://818.pt/media/quarto3_2.jpeg",
      "https://818.pt/media/quarto3_3.jpeg",
      bungalowImages[1],
    ],
  },
];

// Header Component
const Header = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 header-blur shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
<a href="#home" className="flex items-center gap-3" data-testid="logo">
  <img 
    src="https://818.pt/media/logo.png" 
    alt="818 Centro Náutico Logo" 
    className="w-auto h-12 object-contain" // Ajustei para w-10 para o logo ter mais destaque
  />
</a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {["Início", "Bungalows", "Atividades", "Galeria", "Localização", "Contacto"].map((item, index) => (
              <a
                key={item}
                href={`#${["home", "bungalows", "activities", "gallery", "location", "contact"][index]}`}
                className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-nautical-accent ${
                  isScrolled ? "text-nautical-text" : "text-white"
                }`}
                data-testid={`nav-${item.toLowerCase()}`}
              >
                {item}
              </a>
            ))}
            <a
              href={RESERVATION_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reserve-btn-header"
              className="btn-nautical bg-nautical-dark text-white hover:bg-nautical-text px-6 py-3 text-xs font-semibold tracking-widest uppercase transition-all duration-300"
            >
              Reservar
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? "text-nautical-dark" : "text-white"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? "text-nautical-dark" : "text-white"}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-nautical-border"
            data-testid="mobile-menu"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {["Início", "Bungalows", "Atividades", "Galeria", "Localização", "Contacto"].map((item, index) => (
                <a
                  key={item}
                  href={`#${["home", "bungalows", "activities", "gallery", "location", "contact"][index]}`}
                  className="text-nautical-text text-base font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item}
                </a>
              ))}
              <a
                href={RESERVATION_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reserve-btn-mobile"
                className="btn-nautical bg-nautical-dark text-white text-center py-4 text-sm font-semibold tracking-widest uppercase mt-4"
                onClick={() => setMobileMenuOpen(false)}
              >
                Reservar
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
  {/* Vídeo de Fundo */}
  <video
    autoPlay
    loop
    muted
    playsInline
    className="absolute top-0 left-0 w-full h-full object-cover"
  >
    <source src="https://818.pt/media/CentroNautico2.mp4" type="video/mp4" />
    O seu navegador não suporta vídeos.
  </video>

  {/* Overlay Escuro (para o texto continuar legível) */}
  <div className="absolute top-0 left-0 w-full h-full bg-black/40" />

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 max-w-4xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-sm md:text-base tracking-widest uppercase text-white/90 mb-6"
        >
          Alojamento Local à Beira Rio
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-serif text-5xl md:text-7xl lg:text-8xl font-semibold tracking-tight mb-6"
        >
          818 Centro Náutico<span className="text-2xl md:text-3xl align-super">®</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-lg md:text-xl text-white/90 mb-10 font-light max-w-2xl mx-auto"
        >
          Onde a natureza encontra o conforto. Um refúgio junto ao rio.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a
            href={RESERVATION_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-reserve-btn"
            className="btn-nautical bg-white text-nautical-dark hover:bg-nautical-light px-10 py-4 text-sm font-semibold tracking-widest uppercase transition-all duration-300"
          >
            Reservar Agora
          </a>
          <a
            href="#bungalows"
            data-testid="hero-explore-btn"
            className="border border-white text-white hover:bg-white/10 px-10 py-4 text-sm font-semibold tracking-widest uppercase transition-all duration-300"
          >
            Explorar
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full mt-2"
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    { icon: Waves, title: "À Beira Rio", desc: "Localização privilegiada junto ao rio" },
    { icon: Home, title: "Bungalows de Luxo", desc: "Acomodações confortáveis e modernas" },
    { icon: Users, title: "Experiência Única", desc: "Momentos inesquecíveis em família" },
    { icon: Anchor, title: "Atividades Náuticas", desc: "Desportos aquáticos disponíveis" },
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm tracking-widest uppercase text-nautical-muted font-semibold mb-4"
          >
            Bem-vindo ao paraíso
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-5xl text-nautical-dark tracking-tight mb-6"
          >
            O Seu Refúgio à Beira Rio
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-nautical-muted leading-relaxed"
          >
            O 818 Centro Náutico oferece uma experiência única de alojamento junto ao rio.
            Com bungalows confortáveis e uma localização privilegiada, proporcionamos
            o cenário perfeito para umas férias memoráveis.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6 bg-nautical-light rounded-lg"
              data-testid={`feature-${index}`}
            >
              <feature.icon className="w-10 h-10 text-nautical-accent mx-auto mb-4" />
              <h3 className="font-serif text-lg text-nautical-dark mb-2">{feature.title}</h3>
              <p className="text-sm text-nautical-muted">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Bungalows Section
const BungalowsSection = () => {
  const [selectedBungalow, setSelectedBungalow] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const openBungalowPopup = (bungalow) => {
    setSelectedBungalow(bungalow);
    setCurrentPhotoIndex(0);
  };

  const nextPhoto = () => {
    if (selectedBungalow) {
      setCurrentPhotoIndex((prev) => (prev + 1) % selectedBungalow.photos.length);
    }
  };

  const prevPhoto = () => {
    if (selectedBungalow) {
      setCurrentPhotoIndex((prev) => (prev - 1 + selectedBungalow.photos.length) % selectedBungalow.photos.length);
    }
  };

  return (
    <section id="bungalows" data-testid="bungalows-section" className="py-24 md:py-32 bg-nautical-light">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm tracking-widest uppercase text-nautical-muted font-semibold mb-4"
          >
            Acomodações
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-5xl text-nautical-dark tracking-tight"
          >
            Os Nossos Bungalows
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {bungalowTypes.map((bungalow, index) => (
            <motion.div
              key={bungalow.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
              data-testid={`bungalow-card-${bungalow.id}`}
            >
              <div 
                className="relative h-64 overflow-hidden cursor-pointer"
                onClick={() => openBungalowPopup(bungalow)}
              >
                <img
                  src={bungalow.image}
                  alt={bungalow.name}
                  className="w-full h-full object-cover image-hover group-hover:scale-105 transition-transform duration-500"
                />
                {bungalow.hasSeaView && (
                  <div className="absolute top-4 right-4 bg-nautical-accent text-white px-3 py-1.5 flex items-center gap-1.5">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs font-semibold tracking-wider">Vista Mar</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 bg-white/90 px-3 py-1.5">
                  <span className="text-xs font-semibold tracking-wider text-nautical-dark">
                    {bungalow.category}
                  </span>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-nautical-dark/0 group-hover:bg-nautical-dark/20 transition-colors duration-300 flex items-center justify-center">
                  <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wider uppercase">
                    Ver Fotos
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl text-nautical-dark mb-4">{bungalow.name}</h3>
                
                {/* Specs */}
                <div className="flex flex-wrap gap-4 mb-4 text-nautical-muted">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{bungalow.maxGuests} máx. hóspedes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Maximize className="w-4 h-4" />
                    <span className="text-sm">{bungalow.area} m²</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bed className="w-4 h-4" />
                    <span className="text-sm">{bungalow.type}</span>
                  </div>
                </div>
                
                <p className="text-sm text-nautical-muted mb-6 line-clamp-3">
                  {bungalow.description}
                </p>
                
                <a
                  href={RESERVATION_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`reserve-btn-bungalow-${bungalow.id}`}
                  className="btn-nautical inline-block w-full text-center bg-nautical-dark text-white hover:bg-nautical-text px-6 py-3 text-xs font-semibold tracking-widest uppercase transition-all duration-300"
                >
                  Reservar
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bungalow Photos Popup */}
      <Dialog open={!!selectedBungalow} onOpenChange={() => setSelectedBungalow(null)}>
        <DialogContent className="max-w-4xl bg-white p-0 overflow-hidden" data-testid="bungalow-popup">
          <DialogTitle className="sr-only">
            {selectedBungalow?.name} - Galeria de Fotos
          </DialogTitle>
          <DialogDescription className="sr-only">
            Galeria de fotos do {selectedBungalow?.name}
          </DialogDescription>
          
          {selectedBungalow && (
            <div className="flex flex-col">
              {/* Image Gallery */}
              <div className="relative h-[50vh] md:h-[60vh] bg-nautical-surface">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentPhotoIndex}
                    src={selectedBungalow.photos[currentPhotoIndex]}
                    alt={`${selectedBungalow.name} - Foto ${currentPhotoIndex + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>

                {/* Navigation Buttons */}
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
                  data-testid="bungalow-popup-prev"
                >
                  <ChevronLeft className="w-5 h-5 text-nautical-dark" />
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
                  data-testid="bungalow-popup-next"
                >
                  <ChevronRight className="w-5 h-5 text-nautical-dark" />
                </button>

                {/* Photo counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 px-4 py-2 rounded-full">
                  <span className="text-sm text-nautical-dark font-medium">
                    {currentPhotoIndex + 1} / {selectedBungalow.photos.length}
                  </span>
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="flex gap-2 p-4 bg-nautical-surface overflow-x-auto">
                {selectedBungalow.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 overflow-hidden transition-all ${
                      currentPhotoIndex === index 
                        ? "ring-2 ring-nautical-accent" 
                        : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Info & CTA */}
              <div className="p-6 bg-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-2xl text-nautical-dark mb-2">
                      {selectedBungalow.name}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-nautical-muted text-sm">
                      <span>{selectedBungalow.maxGuests} máx. hóspedes</span>
                      <span>{selectedBungalow.area} m²</span>
                      <span>{selectedBungalow.type}</span>
                    </div>
                  </div>
                  <a
                    href={RESERVATION_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="bungalow-popup-reserve"
                    className="btn-nautical inline-block bg-nautical-dark text-white hover:bg-nautical-text px-8 py-4 text-sm font-semibold tracking-widest uppercase transition-all duration-300 text-center"
                    onClick={() => setSelectedBungalow(null)}
                  >
                    Reservar Este Bungalow
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Activities Section - NEW
const ActivitiesSection = () => {
  const activities = [
    { icon: Ship, title: "Passeios de Barco", desc: "Explore o rio com passeios guiados" },
    { icon: Waves, title: "Kayak & Paddle", desc: "Aventuras aquáticas para todos" },
    { icon: Compass, title: "Percursos Náuticos", desc: "Descubra paisagens únicas" },
  ];

  return (
    <section id="activities" data-testid="activities-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative h-[400px] md:h-[500px] overflow-hidden"
          >
            <img
              src="https://818.pt/media/sueste.jpg"
              alt="Atividades náuticas"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-widest uppercase text-nautical-muted font-semibold mb-4">
              Experiências
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-nautical-dark tracking-tight mb-6">
              Atividades Náuticas
            </h2>
            <p className="text-base md:text-lg text-nautical-muted leading-relaxed mb-8">
              Descubra um mundo de aventuras aquáticas com os nossos parceiros da NautiTours. 
              Desde passeios tranquilos de barco até experiências de kayak e paddle, 
              há algo para todos os gostos.
            </p>

            <div className="space-y-6 mb-8">
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="bg-nautical-light p-3 rounded-lg">
                    <activity.icon className="w-6 h-6 text-nautical-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-nautical-dark mb-1">{activity.title}</h3>
                    <p className="text-sm text-nautical-muted">{activity.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <a
              href="https://nautitours.pt"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nautitours-link"
              className="btn-nautical inline-flex items-center gap-2 bg-nautical-dark text-white hover:bg-nautical-text px-8 py-4 text-sm font-semibold tracking-widest uppercase transition-all duration-300"
            >
              Ver Atividades
              <ExternalLink className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section with Lightbox
const GallerySection = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-nautical-light">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm tracking-widest uppercase text-nautical-muted font-semibold mb-4"
          >
            Momentos
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-5xl text-nautical-dark tracking-tight"
          >
            Galeria
          </motion.h2>
        </div>

        <div className="masonry-grid">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`masonry-item cursor-pointer overflow-hidden ${
                index === 0 ? "row-span-2 col-span-2 md:col-span-2" : ""
              } ${index === 4 ? "col-span-2 md:col-span-2" : ""}`}
              onClick={() => openLightbox(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image}
                alt={`Galeria ${index + 1}`}
                className="w-full h-full object-cover image-hover hover:scale-105 transition-transform duration-500"
                style={{ minHeight: index === 0 ? "400px" : "200px" }}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl bg-black/95 border-none p-0" data-testid="lightbox-dialog">
          <DialogTitle className="sr-only">Imagem da galeria</DialogTitle>
          <DialogDescription className="sr-only">Visualização em tela cheia da imagem da galeria</DialogDescription>
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImageIndex}
                src={galleryImages[currentImageIndex]}
                alt={`Galeria ${currentImageIndex + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="max-w-full max-h-full object-contain"
              />
            </AnimatePresence>

            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-3 rounded-full transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  return (
    <section id="location" data-testid="location-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-widest uppercase text-nautical-muted font-semibold mb-4">
              Localização
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-nautical-dark tracking-tight mb-6">
              Como Chegar
            </h2>
            <p className="text-base md:text-lg text-nautical-muted leading-relaxed mb-8">
              O 818 Centro Náutico está situado numa localização privilegiada à beira rio,
              proporcionando fácil acesso e um ambiente tranquilo para os nossos hóspedes.
            </p>
            <div className="flex items-start gap-4 mb-6">
              <MapPin className="w-6 h-6 text-nautical-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-nautical-dark mb-1">Morada</h3>
                <p className="text-nautical-muted">
                  818 Centro Náutico<br />
                  Portugal
                </p>
              </div>
            </div>
            <a
              href="https://maps.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-nautical-accent font-medium hover:text-nautical-dark transition-colors"
              data-testid="directions-link"
            >
              Obter direções →
            </a>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="h-[400px] md:h-[500px] overflow-hidden"
          >
            <iframe
              src="https://www.google.com/maps?q=37.1816262,-7.4104558&z=19&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização 818 Centro Náutico"
              className="map-grayscale"
              data-testid="location-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-nautical-dark text-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm tracking-widest uppercase text-nautical-accent font-semibold mb-4"
          >
            Contacto
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-5xl tracking-tight mb-6"
          >
            Fale Connosco
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-white/70 leading-relaxed mb-12"
          >
            Tem alguma questão ou pretende fazer uma reserva?
            Entre em contacto connosco através de WhatsApp ou email.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12"
          >
            {/* WhatsApp */}
            <a
              href="https://wa.me/351968661072"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-white/10 hover:bg-white/20 px-8 py-5 transition-colors group"
              data-testid="whatsapp-link"
            >
              <Phone className="w-6 h-6 text-nautical-accent" />
              <div className="text-left">
                <span className="text-xs text-white/60 uppercase tracking-wider block">WhatsApp</span>
                <span className="text-lg font-medium group-hover:text-nautical-accent transition-colors">
                  +351 968 661 072
                </span>
              </div>
            </a>

            {/* Email */}
            <a
              href="mailto:818@818.pt"
              className="flex items-center gap-4 bg-white/10 hover:bg-white/20 px-8 py-5 transition-colors group"
              data-testid="email-link"
            >
              <Mail className="w-6 h-6 text-nautical-accent" />
              <div className="text-left">
                <span className="text-xs text-white/60 uppercase tracking-wider block">Email</span>
                <span className="text-lg font-medium group-hover:text-nautical-accent transition-colors">
                  818@818.pt
                </span>
              </div>
            </a>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <a
              href={RESERVATION_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-reserve-btn"
              className="btn-nautical inline-block bg-white text-nautical-dark hover:bg-nautical-light px-12 py-5 text-sm font-semibold tracking-widest uppercase transition-all duration-300"
            >
              Fazer Reserva
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <footer data-testid="footer" className="bg-nautical-dark border-t border-white/10 py-12">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <a href="#home" className="flex items-center gap-3" data-testid="logo">
  <img 
    src="https://818.pt/media/logo_branco.png" 
    alt="818 Centro Náutico Logo" 
    className="w-auto h-12 object-contain" // Ajustei para w-10 para o logo ter mais destaque
  />
</a>
          </div>

          {/* Links */}
         <div className="flex flex-wrap justify-center gap-6 md:gap-8 text-sm text-white/60">
            <button 
              onClick={() => setPrivacyOpen(true)}
              className="hover:text-white transition-colors"
              data-testid="privacy-link"
            >
              Política de Privacidade
            </button>
            <button 
              onClick={() => setTermsOpen(true)}
              className="hover:text-white transition-colors"
              data-testid="terms-link"
            >
              Termos e Condições
            </button>
            <a 
              href="https://www.livroreclamacoes.pt/inicio/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors flex items-center gap-1"
              data-testid="complaints-book-link"
            >
              Livro de Reclamações
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Copyright */}
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} 818 Centro Náutico®. Todos os direitos reservados.
          </p>
        </div>
      </div>
            <a style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              href="https://www.qwerty-cw.pt" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white transition-colors flex items-center gap-1"
              data-testid="complaints-book-link"
            >
              Desenvolvido por QWERTY - Computing World
              <ExternalLink className="w-3 h-3" />
            </a>

    {/* Privacy Policy Dialog */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="privacy-dialog">
          <DialogTitle className="font-serif text-2xl text-nautical-dark mb-4">
            Política de Privacidade
          </DialogTitle>
          <DialogDescription className="sr-only">Política de privacidade do 818 Centro Náutico</DialogDescription>
          <div className="text-nautical-muted text-sm space-y-4">
            <p><strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-PT')}</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">1. Responsável pelo Tratamento</h3>
            <p>O 818 Centro Náutico é o responsável pelo tratamento dos dados pessoais recolhidos através deste website.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">2. Dados Pessoais Recolhidos</h3>
            <p>Podemos recolher os seguintes dados pessoais:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Nome completo</li>
              <li>Endereço de email</li>
              <li>Número de telefone</li>
              <li>Dados de reserva (datas, número de hóspedes)</li>
            </ul>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">3. Finalidade do Tratamento</h3>
            <p>Os dados pessoais são tratados para:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Processar reservas e pedidos de informação</li>
              <li>Comunicar informações sobre os nossos serviços</li>
              <li>Cumprir obrigações legais</li>
            </ul>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">4. Partilha de Dados</h3>
            <p>Os seus dados pessoais não serão partilhados com terceiros, exceto quando necessário para a prestação dos nossos serviços ou quando exigido por lei.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">5. Direitos do Titular</h3>
            <p>Tem direito a aceder, retificar, apagar ou limitar o tratamento dos seus dados pessoais. Para exercer estes direitos, contacte-nos através do email 818@818.pt.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">6. Cookies</h3>
            <p>Este website utiliza cookies para melhorar a experiência de navegação e para fins analíticos.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">7. Contacto</h3>
            <p>Para questões relacionadas com privacidade, contacte-nos:</p>
            <p>Email: 818@818.pt<br />Telefone: +351 968 661 072</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions Dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="terms-dialog">
          <DialogTitle className="font-serif text-2xl text-nautical-dark mb-4">
            Termos e Condições
          </DialogTitle>
          <DialogDescription className="sr-only">Termos e condições do 818 Centro Náutico</DialogDescription>
          <div className="text-nautical-muted text-sm space-y-4">
            <p><strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-PT')}</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">1. Disposições Gerais</h3>
            <p>Os presentes Termos e Condições regulam a utilização do website e os serviços de alojamento prestados pelo 818 Centro Náutico.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">2. Reservas</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>As reservas estão sujeitas a disponibilidade e confirmação</li>
              <li>O check-in realiza-se a partir das 15h00 e o check-out até às 11h00</li>
              <li>É necessária a apresentação de documento de identificação válido no check-in</li>
            </ul>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">3. Cancelamentos</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Cancelamentos até 7 dias antes: reembolso total</li>
              <li>Cancelamentos entre 3 a 7 dias: reembolso de 50%</li>
              <li>Cancelamentos com menos de 3 dias: sem reembolso</li>
            </ul>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">4. Regras do Alojamento</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Não é permitido fumar no interior dos bungalows</li>
              <li>Animais de estimação mediante consulta prévia</li>
              <li>Respeitar o silêncio entre as 23h00 e as 08h00</li>
              <li>O hóspede é responsável por quaisquer danos causados</li>
            </ul>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">5. Responsabilidade</h3>
            <p>O 818 Centro Náutico não se responsabiliza por objetos de valor deixados nos alojamentos. Recomendamos a utilização dos cofres disponíveis.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">6. Reclamações</h3>
            <p>Para apresentar uma reclamação, pode utilizar o Livro de Reclamações eletrónico disponível em <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noopener noreferrer" className="text-nautical-accent hover:underline">www.livroreclamacoes.pt</a>.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">7. Lei Aplicável</h3>
            <p>Os presentes termos são regidos pela lei portuguesa. Qualquer litígio será submetido aos tribunais portugueses competentes.</p>
            
            <h3 className="font-semibold text-nautical-dark text-base mt-6">8. Contacto</h3>
            <p>Email: 818@818.pt<br />Telefone: +351 968 661 072</p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App font-sans" data-testid="app-container">
      <Header
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <AboutSection />
        <BungalowsSection />
        <ActivitiesSection />
        <GallerySection />
        <LocationSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
